import React from "react";
import InputField from "../../UI/InputField/InputField";
import TextArea from "../../UI/TextArea/TextArea";
import DropdownSearch from "../../UI/DropdownSearch/DropdownSearch";
import {
  betweenNChars,
  blockSpecChar,
  detectIntWithSpace,
  nomineeAddressRestrictedExp,
  nomineeEnglishRegularLanguage,
} from "../../utils/regex";
import { IListObj } from "../../types/genericTypes";
import {
  AGetArea,
  AGetCityByPincode,
  AGetFullAddress,
} from "../../Services/dashboard/profileApiService";
import Checkbox from "../../UI/Checkbox/Checkbox";
import { errorCode, errorMsg } from "../../../ErrorCode/ErrorCode";
import GlobalStore from "../../store/globalStore";
import { getNriAccountType } from "../../utils/entitySubType";

export interface AddressFieldProps {
  addressType: "permanent" | "correspondance" | "nominee" | "guardian";
  sectionLabel: string;
  addressObj: IAddressFormat;
  isDisable?: boolean;
  showSameAsAbove?: boolean;
  sameAsAbove?: boolean;
  handleSameAsAbove: (value: React.ChangeEvent<HTMLInputElement>) => void;
  setErrCode: (value: number | string) => void;
  onAddressChange: (addressObj: IAddressFormat) => void;
  onAddressBlur: (addressObj: {
    [key in keyof IAddressFormat]?: string;
  }) => void;
  forNomineeAddress?: boolean;
  enableCity?: boolean;
  addressTitle?: string;
  showArea?: boolean;
  showCountry?: boolean;
  showCity?: boolean;
  countryList?: Array<IListObj>;
  showPincode?: boolean;
  getNriAccountLabel?: any;
}

export interface IAddressFormat {
  addressLine1: string;
  City: string;
  Country: string;
  District: string;
  PinCode: string;
  State: string;
  Area?: string;
  CountryCode?: number;
  StateCode?: number;
}

export interface CkycAddressFormat {
  addressLine1: string;
  corrAddress?: string;
  sameAsPermanent: boolean;
  city: string;
  country: string;
  district: string;
  pincode: string;
  corrPincode?: string;
  state: string;
  area?: string;
  countrycode?: number;
  statecode?: number;
  corrCountry?: string;
  perCountry?: string;
}

export const addressErrorObj = {
  addressLine1: undefined,
  pincode: undefined,
  area: undefined,
};

export const addressErrorMsg = {
  // addressLine1:
  //     'Please enter valid address. Address should contains minimum 5 characters and Address line should not contain  ~ ` # $ % ^ & * ( ) = + { } [ ] \\ | : ; ” ’ < > , ? /',
  addressLine1:
    "Special Characters are not allowed and Address should contains minimum 5 characters",
  // pincode: 'Please enter 6 digit pincode',
  pincode: "Enter 6 Digit Pin Code",
  // area: 'Please select a area from dropdown',
  area: "Type first 3 characters of city",
};

const AddressField: React.FC<AddressFieldProps> = (props) => {
  const [addressObj, setAddressObj] = React.useState<IAddressFormat>(
    props.addressObj
  );
  const [areaList, setAreaList] = React.useState<Array<IListObj>>([]);
  const [validPincode, setValidPincode] = React.useState<string>("");
  const [errors, setErrors] = React.useState<{
    [key in keyof IAddressFormat]?: string;
  }>({ ...addressErrorObj });

  React.useEffect(() => {
    // NRE/NRO foreign address: pin/zip is free-text and must not hit Indian
    // MastersData pincode APIs (empty response sets errCode 131 and blocks submit).
    if (props?.getNriAccountLabel) {
      setAddressObj(props.addressObj);
      return;
    }
    if (
      JSON.stringify(addressObj) !== JSON.stringify(props?.addressObj) ||
      addressObj?.PinCode === props?.addressObj?.PinCode
    ) {
      setAddressObj(props.addressObj);
      if (addressObj?.Area !== undefined && addressObj?.Area !== null) {
        if (addressObj?.Area == "") {
          if (
            props?.addressObj?.PinCode &&
            // addressObj?.addressLine1?.length == 0 &&
            props?.addressObj?.PinCode?.length === 6
          ) {
            setAreaList([]);
            setValidPincode("");
            // AGetArea((res: any) => {
            //   getAreaList(res, props.addressObj.PinCode);
            // }, props.addressObj.PinCode);
            AGetCityByPincode(
              (res: any) => getFullAddress(res),
              props.addressObj.PinCode
            );
          }
        }
      } else {
        if (
          props?.addressObj?.PinCode &&
          // addressObj?.addressLine1?.length == 0 &&
          props?.addressObj?.PinCode?.length === 6
        ) {
          setAreaList([]);
          setValidPincode("");
          // AGetArea((res: any) => {
          //   getAreaList(res, props.addressObj.PinCode);
          // }, props.addressObj.PinCode);
          AGetCityByPincode(
            (res: any) => getFullAddress(res),
            props.addressObj.PinCode
          );
        }
      }
    }
  }, [
    props?.addressObj?.addressLine1 != addressObj?.addressLine1,
    props?.addressObj?.PinCode !== addressObj?.PinCode,
    props?.getNriAccountLabel,
  ]);

  function handleChangeAddress(
    key: keyof IAddressFormat,
    value: string,
    extra?: Partial<IAddressFormat>
  ) {
    // debugger
    if (
      key === "PinCode" &&
      !props?.getNriAccountLabel &&
      value.length === 6
    ) {
      setAreaList([]);
      props?.setErrCode("");
      AGetCityByPincode((res: any) => getFullAddress(res), value);
      setErrors({ ...errors, [key]: undefined });
      setValidPincode("");
    }
    if (key === "Area" && !props?.getNriAccountLabel) {
      AGetFullAddress(getFullAddress, value, addressObj?.PinCode);
      setErrors({ ...errors, [key]: undefined });
    }
    setAddressObj({ ...addressObj, [key]: value, ...extra });
    props.onAddressChange({ ...props.addressObj, [key]: value, ...extra });
    if (key === "PinCode" || key === "Area") {
      props.onAddressBlur({ ...errors, [key]: undefined });
    }

    if (key === "addressLine1") {
      if (detectIntWithSpace.test(value)) {
        // Address line should not contains only numbers
        setErrors({
          ...errors,
          addressLine1: errorMsg(errorCode.FE17),
        });
        props.onAddressBlur({
          ...errors,
          addressLine1: errorMsg(errorCode.FE17),
        });
      } else if (
        !!props.forNomineeAddress &&
        props?.forNomineeAddress == true
      ) {
        if (nomineeAddressRestrictedExp.test(value)) {
          setErrors({
            ...errors,
            addressLine1:
              errorMsg(errorCode.FE58),
          });
        } else if (!nomineeEnglishRegularLanguage.test(value)) {
          setErrors({
            ...errors,
            addressLine1:
              errorMsg(errorCode.FE59),
          });
        } else {
          setErrors({ ...errors, addressLine1: undefined });
          props.onAddressBlur({ ...errors, addressLine1: undefined });
        }
      }
      else {
        // Need to clear
        setErrors({ ...errors, addressLine1: undefined });
        props.onAddressBlur({ ...errors, addressLine1: undefined });
      }
    }
  }

  function getAreaList(res: any, pincode: string) {
    if (!res || res.error) {
      // props.setErrCode('130 - Failed to fetch the area list for the pincode');
      props.setErrCode(res.error);
    } else {
      if (res.data && res.data.areas) {
        // if (res.data.areas[0] !== "Area not exist.") {  // Rohit -- commented on 13-08-2022
        if (res.data.areas.length > 0) {
          let arr: Array<IListObj> = [];
          res.data.areas.forEach((item: string) => {
            let obj: IListObj = {
              value: item,
              display: item,
            };
            arr.push(obj);
          });
          setAreaList(arr);
          AGetFullAddress(getFullAddress, arr[0].value as string, pincode);
          setValidPincode("");
        } else {
          setValidPincode(errorMsg(errorCode.FE38));
        }
      } else {
        props.setErrCode(res.error);
      }
    }
  }

  function getFullAddress(res: any) {

    // NRI foreign address fields are free-text; do not surface Indian
    // master lookup failures as blocking profile errors.
    if (props?.getNriAccountLabel) {
      return;
    }

    const isNriAccount = GlobalStore.isNriInv || GlobalStore.isNriOtc;

    if (!res || res.error) {
      //   props.setErrCode(
      //     "131 - Failed to fetch details corresponding to pincode and area"
      //   );
      // NRE/NRO: keep page usable; do not block Submit with master lookup errors
      if (isNriAccount) {
        setValidPincode(errorMsg(errorCode.FE38));
        return;
      }
      props.setErrCode(res.error);
    } else {
      if (res.data && res.data.length > 0) {
        const obj: any = {};
        obj.PinCode = res.data[0].PinCode;
        obj.Area = res.data[0].City;
        obj.District =
          res.data[0].District == "" ? res.data[0].City : res.data[0].District;
        obj.Country = res.data[0].Country;
        obj.City = res.data[0].City;
        obj.State = res.data[0].State;
        obj.StateCode = res.data[0].StateCode;
        obj.CountryCode = res.data[0].CountryCode;
        setAddressObj({ ...props.addressObj, ...obj });
        props.onAddressChange({ ...props.addressObj, ...obj });
        setValidPincode("");
        props.setErrCode("");
      } else {
        // NRE/NRO: empty master response must not wipe address or block submit
        if (isNriAccount) {
          setValidPincode(errorMsg(errorCode.FE38));
          return;
        }
        const obj: any = {};
        obj.Area = "";
        obj.District = "";
        obj.Country = "";
        obj.City = "";
        obj.State = "";
        obj.StateCode = "";
        obj.CountryCode = "";
        setAddressObj({ ...props.addressObj, ...obj });
        props.onAddressChange({ ...props.addressObj, ...obj });
        props.setErrCode(
          "131 - Failed to fetch details corresponding to pincode and area"
        );
      }
    }
  }

  function handleBlurAddress(key: keyof IAddressFormat, value: any) {
    switch (key) {
      case "addressLine1":
        setErrors({
          ...errors,
          addressLine1: value.pattern ? undefined : errorMsg(errorCode.FE43),
        });
        props.onAddressBlur({
          ...errors,
          addressLine1: value.pattern ? undefined : errorMsg(errorCode.FE43),
        });
        break;
      case "PinCode":
        setErrors({
          ...errors,
          PinCode: value.pattern ? undefined : errorMsg(errorCode.FE18),
        });
        props.onAddressBlur({
          ...errors,
          PinCode: value.pattern ? undefined : errorMsg(errorCode.FE18),
        });
        break;
      case "Area":
        if (typeof value === "object") {
          setErrors({ ...errors, Area: undefined });
          props.onAddressBlur({ ...errors, Area: undefined });
        } else {
          setErrors({ ...errors, Area: errorMsg(errorCode.FE44) });
          props.onAddressBlur({ ...errors, Area: errorMsg(errorCode.FE44) });
        }
        break;
    }
  }

  return (
    <>
      <div className="mgt30">
        <label className="sectionLbl">
          <strong>{props.sectionLabel}</strong>
          {props.showCountry && (
            <span className="requiredMark">
              {" "}*
            </span>
          )}
        </label>
        {props.showSameAsAbove && (
          <span className="sectionLbl">
            <label className="fieldLblRight" htmlFor={`sameAs${props.addressType}`}>Same as above</label>
            <div className="crrAddCheckBox fieldLblRight">
              <Checkbox
                id={`sameAs${props.addressType}`}
                checked={props.sameAsAbove || false}
                name={`sameAs${props.addressType}`}
                onChange={props.handleSameAsAbove}
              />
            </div>
          </span>
        )}
      </div>
      {(!(props.addressType === "correspondance") || !props.sameAsAbove) && (
        <>
          <div className="mgt30">
            <label className="fieldLbl" htmlFor={`address${props.addressType}`}>{props?.addressTitle ? props?.addressTitle : "Address"}</label>
            <TextArea
              // onBlur={event => {
              //     // handleBlurAddress('addressLine1', error)
              // }}
              // addressAlphaNum
              id={`address${props.addressType}`}
              defaultValue={addressObj?.addressLine1}
              placeholder="Eg. Address"
              onChange={(value) => handleChangeAddress("addressLine1", value)}
              disabled={props.isDisable}
              errorText={errors.addressLine1}
              name={`address${props.addressType}`}
              maxLength={140}
            />
          </div>

          {(props.showPincode || props.showPincode == undefined) &&
            <div className="mgt40">
              <label className="fieldLbl">{getNriAccountType(props?.getNriAccountLabel)} Pin Code</label>
              <InputField
                pattern={props?.getNriAccountLabel ? undefined : betweenNChars(6)}
                onBlur={(error) => {
                  if (props?.getNriAccountLabel) {
                    setErrors({ ...errors, PinCode: undefined });
                    props.onAddressBlur({ ...errors, PinCode: undefined });
                    return;
                  }
                  handleBlurAddress("PinCode", error);
                }}
                defaultValue={addressObj?.PinCode}
                placeholder={props?.getNriAccountLabel ? "Eg. 10001" : "Eg. 1233456"}
                onChange={(value) => {
                  props?.setErrCode("");
                  handleChangeAddress("PinCode", value);
                }}
                disabled={props?.getNriAccountLabel ? false : props.isDisable}
                // integer
                maxLength={props?.getNriAccountLabel ? 100 : 6}
                errorText={errors.PinCode || validPincode}
                name={`pinCode${props.addressType}`}
              />
            </div>
          }

          {props.showCountry &&
            <div className='mgt40'>
              <label className='fieldLbl'>{getNriAccountType(props?.getNriAccountLabel)} Country</label>
              <DropdownSearch
                name={`country${props.addressType}`}
                defaultValue={addressObj?.Country}
                disabled={props.isDisable}
                onOptionSelect={(value) =>
                  handleChangeAddress("Country", value["display"] as string, {
                    CountryCode: Number(value["value"]),
                  })
                }
                searchLength={0}
                options={props.countryList}
                onBlur={(val) => handleBlurAddress("Country", val)}
                searchKey="display"
                placeholder={(GlobalStore.isNriInv || GlobalStore.isNriOtc) ? "Eg. USA" : "Eg. India"}
                noDataMsg="Country with similar name not found"
                errorText={errors.Country}
                isDownArrow={true}
              />
            </div>
          }
          {props?.getNriAccountLabel && (
            <div className="mgt40">
              <label className="fieldLbl">
                {getNriAccountType(props?.getNriAccountLabel)} State
              </label>
              <InputField
                defaultValue={addressObj?.State}
                placeholder="Eg. California"
                onChange={(value) => handleChangeAddress("State", value)}
                disabled={props.isDisable}
                name={`state${props.addressType}`}
                maxLength={100}
                errorText={errors.State}
              />
            </div>
          )}
          {props?.getNriAccountLabel && (
            <div className="mgt40">
              <label className="fieldLbl">
                {getNriAccountType(props?.getNriAccountLabel)} City
              </label>
              <InputField
                defaultValue={addressObj?.City}
                placeholder="Eg. New York"
                onChange={(value) => handleChangeAddress("City", value)}
                disabled={props.isDisable}
                name={`city${props.addressType}`}
                maxLength={100}
                errorText={errors.City}
              />
            </div>
          )}

          {(props.showCity || props.showCity == undefined) && !props?.getNriAccountLabel &&
            <div className="mgt40">
              <label className="fieldLbl" htmlFor={`cityState${props.addressType}`}>City/State</label>
              <InputField
                id={`cityState${props.addressType}`}
                defaultValue={
                  addressObj?.City || addressObj?.State
                    ? addressObj?.City + " / " + addressObj?.State
                    : ""
                }
                placeholder={(GlobalStore.isNriInv || GlobalStore.isNriOtc) ? "Eg. Washington/New York" : "Eg. Mumbai/Maharashtra"}
                disabled={props?.enableCity ? false : true}
                type="text"
                name={`cityState${props.addressType}`}
              />
            </div>
          }

          {props.showArea &&
            <div className="mgt40">
              <label className="fieldLbl">Area</label>
              <DropdownSearch
                name={`area${props.addressType}`}
                defaultValue={addressObj?.Area}
                disabled={addressObj?.PinCode?.length < 6 || areaList.length < 1}
                onOptionSelect={(value) =>
                  handleChangeAddress("Area", value["value"] as string)
                }
                searchLength={0}
                options={areaList}
                onBlur={(val) => handleBlurAddress("Area", val)}
                searchKey="display"
                placeholder="Eg. Thane"
                noDataMsg="Area with similar name not found"
                errorText={errors.Area}
                isDownArrow={true}
              />
            </div>
          }
        </>
      )}
    </>
  );
};

export default AddressField;
